import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  ScrollView,
  ActivityIndicator,
  RefreshControl,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { format } from 'date-fns';
import Text from '@components/CustomText';
import { ScaledSheet } from '@utils/scale';

import DatePicker from '../components/DatePicker';
import EarningsCard from '../components/EarningsCard';
import IncentiveProgress from '../components/IncentiveProgress';
import ReconPrompt from '../components/ReconPrompt';
import StatsCard from '../components/StatsCard';
import BreakdownList from '../components/BreakdownList';
import TransactionHistoryLink from '../components/TransactionHistoryLink';
import { EarningsData } from '../types';
import IncentiveReceived from '../components/IncentiveReceived';
import IncentiveUnlocked from '../components/IncentiveUnlocked';
import IncentiveMissed from '../components/IncentiveMissed';
import { getDailyEarnings } from '@services/api';
import { formatDateKey, getWeekKeyFromDate, checkIfDateIsWeekKey, getDateFromWeekKey } from '../utils';

const DailyTabContainer: React.FC<{ date: string }> = ({ date }) => {
  const navigation = useNavigation();
  // `date` may arrive as a week key (e.g. from the Weekly tab's nav params);
  // Daily always expects a single day key, so convert it first.
  const resolvedDate = checkIfDateIsWeekKey(date) ? getDateFromWeekKey(date) : (date || '');
  // formatDateKey returns '' for anything that isn't a valid date (garbage nav
  // params, etc.) - fall back to no default rather than carrying an
  // unparseable key into state, which would later crash the calendar's
  // date-fns `format()` calls.
  const defaultDateValue = resolvedDate ? formatDateKey(resolvedDate) : '';
  const defaultDate = defaultDateValue ? resolvedDate : '';
  const [selectedDate, setSelectedDate] = useState<{ key: string; value: string }>({ key: defaultDate, value: defaultDateValue });
  const [earningsData, setEarningsData] = useState<any | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);

  const loadEarningsData = useCallback(async (date: string = '') => {
    try {
      setLoading(true);
      const { data } = await getDailyEarnings(date);
      console.log('Daily Earnings data:', data);
      setEarningsData(data);
    } catch (error) {
      console.error('Error loading daily earnings data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      loadEarningsData(selectedDate.key || '');
    }, [loadEarningsData, selectedDate.key]),
  );

  useEffect(() => {
    if (selectedDate.key) {
      loadEarningsData(selectedDate.key);
    }
  }, [selectedDate.key, loadEarningsData]);

  const onRefresh = async () => {
    setRefreshing(true);
    await loadEarningsData(selectedDate.key);
    setRefreshing(false);
  };

  const handleDateChange = (date: Date) => {
    setSelectedDate({ key: format(date, 'yyyy-MM-dd'), value: format(date, 'd MMM yyyy') });
  };

  const handleViewReconDetails = () => {
    // @ts-ignore - Navigation typing
    navigation.navigate('ReconInfo', { 
      nav_params: { 
        recon_date: selectedDate.key
      } 
    });
  };

  const handleViewTransactionHistory = () => {
    const dateRange = getWeekKeyFromDate(selectedDate.key);
    // @ts-ignore - Navigation typing
    navigation.navigate('TransactionStatements', { date_range: dateRange });
  };

  if (loading && !earningsData) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#000" />
      </View>
    );
  }

  return (
    <ScrollView
      style={styles.scrollView}
      contentContainerStyle={styles.scrollContent}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
      }
      showsVerticalScrollIndicator={false}
    >
      <LinearGradient
        colors={['#FFFFFF', '#F5F5F5', '#E8E8E8']}
        style={styles.mainCard}
        locations={[0, 0.7, 1]}
      >
        <DatePicker
          dateText={selectedDate.value || earningsData?.selected_filter?.value || format(new Date(), 'd MMM yyyy')}
          selectedDate={selectedDate.key ? new Date(selectedDate.key) : new Date()}
          onDateChange={handleDateChange}
          reportType="DAILY"
        />
        <EarningsCard
          reconSummary={earningsData?.recon_summary || {}}
        />
      </LinearGradient>

      {(earningsData?.incentive_summary?.type === 'before_first_milestone') && (
        <View>
          <IncentiveProgress
            htmlContent={earningsData?.incentive_summary?.text || ''}
          />
        </View>
      )}

      {(earningsData?.incentive_summary?.type === 'incentive_received') && (
        <View>
          <IncentiveReceived htmlContent={earningsData?.incentive_summary?.text || ''} />
        </View>
      )}

      {(earningsData?.incentive_summary?.type === 'unlocked_milestone_next') && (
        <View>
          <IncentiveUnlocked htmlContent={earningsData?.incentive_summary?.text || ''} />
        </View>
      )}

      {(earningsData?.incentive_summary?.type === 'missed_first_milestone') && (
        <View>
          <IncentiveMissed htmlContent={earningsData?.incentive_summary?.text || ''} />
        </View>
      )}

      {!!earningsData?.recon_summary?.show_payout_warning && (
        <View style={styles.cardContainer}>
          <ReconPrompt reconText={earningsData?.recon_summary?.payout_warning} reconButtonText={earningsData?.recon_summary?.payout_warning_cta_txt} onViewDetails={handleViewReconDetails} />
        </View>
      )}

      {Array.isArray(earningsData?.earnings_summary) && earningsData?.earnings_summary.length === 3 && <View style={styles.cardContainer}>
        <StatsCard
          earningsPerOrder={earningsData?.earnings_summary[0]}
          totalOrders={earningsData?.earnings_summary[1]}
          totalEarnings={earningsData?.earnings_summary[2]}
        />
      </View>}

      <BreakdownList
        date={earningsData?.recon_summary?.date}
        data={earningsData?.earnings_transactions}
      />

      <View style={styles.dividerContainer}>
        <View style={styles.divider} />
        <View style={styles.dividerTextContainer}>
          <Text style={styles.dividerText}>{earningsData?.footer?.separator || ''}</Text>
        </View>
        <View style={styles.divider} />
      </View>

      {earningsData?.footer?.buttons?.length > 0 && (
        <TransactionHistoryLink title={earningsData?.footer?.buttons[0]?.title} onPress={handleViewTransactionHistory} />
      )}
    </ScrollView>
  );
};

const styles = ScaledSheet.create({
  scrollView: {
    flex: 1,
    backgroundColor: '#EDF0F3',
  },
  scrollContent: {
    paddingBottom: 24,
    backgroundColor: '#EDF0F3',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#EDF0F3',
  },
  mainCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    marginHorizontal: 16,
    marginTop: 16,
    marginBottom: 16,
    paddingTop: 24,
    paddingBottom: 20,
    paddingHorizontal: 20,
    borderWidth: 4,
    borderColor: '#FFF',
  },
  incentiveProgressContainer: {
    marginBottom: 16,
    marginHorizontal: 16,
  },
  cardContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    marginHorizontal: 16,
    marginBottom: 16,
    paddingHorizontal: 20,
    paddingVertical: 20,
  },
  dividerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 16,
    marginVertical: 12,
  },
  divider: {
    flex: 1,
    height: 1,
    backgroundColor: '#E0E0E0',
  },
  dividerTextContainer: {
    paddingHorizontal: 12,
  },
  dividerText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#9E9E9E',
    letterSpacing: 0.5,
  },
});

export default DailyTabContainer;

